// src/pages/LandingPage/index.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, KeyRound } from 'lucide-react';
import { authApi } from '../../api/auth';
import Logo from 'src/assets/images/Logo_weißer Hintergrund.png';
import BgImage from 'src/assets/images/BILD_7.jpg';
import OTPInput from '../../components/ui/otp-input';
import { useAuth } from '../../state/auth/context';


const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await authApi.requestOTP(email);
      setIsOtpSent(true);
      setMessage({
        type: 'success',
        text: 'Ein Code wurde an Ihre E-Mail-Adresse gesendet.'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Der Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!otp || otp.length !== 6) return;

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const sessionToken = await authApi.verifyOTP(email, otp);
      await login(sessionToken);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Ungültiger Code. Bitte versuchen Sie es erneut.'
      });
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    setIsSubmitting(true);
    try {
      await authApi.requestOTP(email);
      setMessage({
        type: 'success',
        text: 'Neuer Code wurde gesendet. Bitte überprüfen Sie Ihre E-Mail.'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Code konnte nicht erneut gesendet werden.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-200 p-4 md:p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 min-h-[calc(100vh-3rem)] bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl overflow-hidden shadow-lg">

        {/* Left: form panel */}
        <div className="flex flex-col p-8 md:p-12">
          {/* Logo top-left */}
          <div className="mb-12">
            <img src={Logo} alt="Logo" className="h-16" />
          </div>

          {/* Form vertically centered, left-aligned with logo */}
          <div className="flex-1 flex items-center">
            <div className="w-full max-w-sm">
              <div className="text-left mb-8">
<h1 className="text-5xl font-semibold text-gray-900 mb-3">
                  {!isOtpSent ? 'Anmelden' : 'Code eingeben'}
                </h1>
                <p className="text-gray-500 text-sm">
                  {!isOtpSent
                    ? 'Wir senden Ihnen einen Code an Ihre E-Mail-Adresse, um sich anzumelden.'
                    : 'Geben Sie den Code ein, der an Ihre E-Mail gesendet wurde'}
                </p>
              </div>

              <form onSubmit={isOtpSent ? handleOtpSubmit : handleEmailSubmit} className="space-y-5">
                {!isOtpSent ? (
                  <div>
<div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4">
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full text-gray-900 bg-transparent border-none outline-none text-sm placeholder-gray-300 focus:ring-0"
                        placeholder="ihre@email.de"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                      Code wurde gesendet an<br />
                      <span className="font-semibold">{email}</span>
                    </label>
                    <OTPInput
                      length={6}
                      disabled={isSubmitting}
                      onComplete={setOtp}
                    />

                    <button
                      type="button"
                      onClick={handleOtpSubmit}
                      disabled={isSubmitting || !otp || otp.length !== 6}
                      className="w-full mt-5 bg-[#7ABB23] text-white py-3.5 px-6 rounded-full
                               hover:bg-[#6aa31f] focus:outline-none focus:ring-2
                               focus:ring-[#7ABB23] focus:ring-offset-2 transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed
                               flex items-center justify-center gap-2 shadow-md"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <KeyRound className="w-5 h-5" />
                          Code verifizieren
                        </>
                      )}
                    </button>

                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setIsOtpSent(false);
                          setOtp('');
                          setMessage({ type: '', text: '' });
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700 mr-4"
                      >
                        E-Mail ändern
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={isSubmitting}
                        className="text-sm text-[#7ABB23] hover:text-[#6aa31f]"
                      >
                        Code erneut senden
                      </button>
                    </div>
                  </div>
                )}

                {message.text && (
                  <div
                    className={`p-4 rounded-2xl ${
                      message.type === 'success'
                        ? 'bg-[#f5fbeb] text-[#7ABB23] border border-[#7ABB23]'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    {message.text}
                  </div>
                )}

                {!isOtpSent && (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#7ABB23] text-white py-3.5 px-6 rounded-full hover:bg-[#6aa31f] focus:outline-none focus:ring-2 focus:ring-[#7ABB23] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                  >
                    {isSubmitting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Code senden
                      </>
                    )}
                  </button>
                )}
              </form>
            </div>
          </div>

          {/* Footer at the bottom of left panel */}
          <div className="flex items-center justify-between text-xs text-gray-500 mt-8">
            <span>© {new Date().getFullYear()} SUSTRAVIA GmbH</span>
            <span className="space-x-4">
              <a href="/Datenschutzerklärung.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">Datenschutz</a>
              <a href="/Impressum.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">Impressum</a>
            </span>
          </div>
        </div>

        {/* Right: image panel */}
        <div className="hidden md:block md:col-span-2 relative m-3 rounded-3xl overflow-hidden shadow-xl">
          <img
            src={BgImage}
            alt="Sustravia"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
